/**
 * Utility functions for audio recording in Expert Interviews.
 */

/**
 * Format duration in seconds to MM:SS format.
 *
 * @param seconds - Duration in seconds
 * @returns Formatted string (e.g., "02:05")
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

/**
 * Generate a UUID v4 session ID.
 *
 * @returns UUID string
 */
export function generateSessionId(): string {
  return crypto.randomUUID()
}

/**
 * Retry a function with exponential backoff.
 *
 * @param fn - Async function to retry
 * @param maxRetries - Maximum number of attempts (default: 3)
 * @returns Promise resolving to function result
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3
): Promise<T> {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn()
    } catch (error) {
      if (i === maxRetries - 1) {
        throw error
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, i) * 1000
      console.warn(`Retry attempt ${i + 1}/${maxRetries}, waiting ${delay}ms...`)
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }

  throw new Error('Unreachable')
}

/**
 * Format file size in bytes to human-readable format.
 *
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

/**
 * Session persistence key for localStorage.
 */
export const ACTIVE_SESSION_KEY = 'expert_interview_active_session'

/**
 * Session data stored in localStorage for crash recovery.
 */
export interface SessionRecoveryData {
  sessionId: string
  startedAt: number
  lastChunkIndex: number
  duration: number
}

/**
 * Save session data to localStorage for crash recovery.
 */
export function saveSessionData(data: SessionRecoveryData): void {
  try {
    localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify(data))
  } catch (error) {
    console.error('Failed to save session data:', error)
  }
}

/**
 * Load session data from localStorage.
 */
export function loadSessionData(): SessionRecoveryData | null {
  try {
    const data = localStorage.getItem(ACTIVE_SESSION_KEY)
    if (!data) return null

    return JSON.parse(data) as SessionRecoveryData
  } catch (error) {
    console.error('Failed to load session data:', error)
    return null
  }
}

/**
 * Clear session data from localStorage.
 */
export function clearSessionData(): void {
  try {
    localStorage.removeItem(ACTIVE_SESSION_KEY)
  } catch (error) {
    console.error('Failed to clear session data:', error)
  }
}

/**
 * True when a stream can still deliver audio to a MediaRecorder (#811).
 *
 * The segment chain builds a NEW MediaRecorder from the same long-lived stream
 * every 30 s, and its guard used to ask whether the stream OBJECT was there.
 * That is not the same question: when a source goes away, the object stays and
 * only its tracks flip to ``ended``. The chain then kept building recorders on
 * a dead stream, produced empty segments, and the #221 watchdog reported it a
 * minute later as a stalled upload — the symptom, not the cause.
 *
 * Rarely reachable with a microphone (a USB headset being unplugged), which is
 * why it went unnoticed; with a screen share as the source it is one click.
 */
export function hasLiveAudioTrack(stream: MediaStream | null | undefined): stream is MediaStream {
  if (!stream) return false
  return stream.getAudioTracks().some((track) => track.readyState === 'live')
}

/** What a MediaRecorder reports about itself; `undefined` when none exists yet. */
export type RecorderState = 'inactive' | 'recording' | 'paused' | undefined

/**
 * Should losing the audio source tear the recording down? (#811)
 *
 * The question is whether the SESSION is still live, which is not the same as
 * whether the current recorder is. One caller is the liveness guard in
 * ``startNewSegment``, and that runs inside the PREVIOUS recorder's ``onstop``
 * — by then it reads ``inactive``. Judging by the recorder alone would make
 * the teardown a no-op on exactly the path that detected the dead stream.
 *
 * ``paused`` has to be read off the recorder because no ref tracks it, and it
 * must count: the stream stays open across a pause, so a source lost while
 * paused would only surface on resume, as a recording that captures nothing.
 */
export function shouldHandleSourceLoss(status: {
  /** The teardown already ran for this recording. */
  alreadyHandled: boolean
  /** ``isRecordingRef`` — the session intends to keep recording. */
  isRecording: boolean
  recorderState: RecorderState
}): boolean {
  if (status.alreadyHandled) return false
  return status.isRecording || status.recorderState === 'paused'
}

/**
 * FFT size for the level analyser. 256 gives 128 bins — what the waveform in
 * `useWaveformBars` draws from, and small enough to stay cheap per frame.
 */
const ANALYSER_FFT_SIZE = 256

/** What a recording captures. Microphone stays the default everywhere (#811). */
export type RecordingSource = 'microphone' | 'microphone+system'

/**
 * The screen share carried no audio track.
 *
 * Measured, not theorised: across 15 share dialogs in the #811 probe runs,
 * four produced no audio because the "share system audio" box was not ticked —
 * by a tester who knew to tick it. This is the likeliest outcome of a first
 * attempt, so it gets its own type and its own message rather than arriving as
 * a generic failure.
 */
export class SystemAudioMissingError extends Error {
  constructor() {
    super('The screen share carried no audio track')
    this.name = 'SystemAudioMissingError'
  }
}

/** Browser APIs this module reaches for, injectable so the setup is testable. */
export interface RecordingMedia {
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>
  getDisplayMedia(options: DisplayMediaStreamOptions): Promise<MediaStream>
  createAudioContext(): AudioContext
}

const browserMedia: RecordingMedia = {
  getUserMedia: (constraints) => navigator.mediaDevices.getUserMedia(constraints),
  getDisplayMedia: (options) => navigator.mediaDevices.getDisplayMedia(options),
  createAudioContext: () => new AudioContext(),
}

/**
 * Constraints for a source that will be MIXED with another.
 *
 * All three off, and that is load-bearing rather than tidy: with echo
 * cancellation on, Chrome filters the loopback away once the microphone joins
 * the same graph and the conference goes silent. Measured with all three off,
 * system audio survived at −33.3 dB beside a −18.7 dB microphone (#811).
 *
 * The cost is the other half of that trade: without echo cancellation, a
 * microphone in front of SPEAKERS records the conference a second time, offset
 * by the room. Headphones are therefore a requirement of this mode, not a tip.
 */
const MIXED_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
}

export interface PreparedRecordingStream {
  /** What the MediaRecorder records. Audio only — a display capture also
   *  carries video, which must never reach the recorder. */
  stream: MediaStream
  context: AudioContext
  /** Already fed by `stream`; drives the waveform. */
  analyser: AnalyserNode
  source: RecordingSource
  /**
   * What the user picked in the share dialog — `monitor`, `window` or
   * `browser`. Only `monitor` captures the whole machine; sharing a tab
   * records that tab alone, which is not what a meeting capture wants, and one
   * of the probe runs did exactly that. Undefined for a microphone recording.
   */
  displaySurface?: string
  /**
   * Level per source. Both start at 1 and nothing moves them yet: the probe
   * measured the system source 14.6 dB below the microphone, but from a test
   * tone at an arbitrary volume — that is a reason to keep the knobs, not a
   * number to bake in. They exist because adding them later means rebuilding
   * the graph while a correction is only a value.
   */
  gains: { microphone: GainNode; system?: GainNode }
  /**
   * Is every source still delivering?
   *
   * Must be asked of the SOURCES, never of `stream`: a MediaStreamDestination
   * track stays `live` forever, including after every input has ended. Asking
   * the mixed stream would report a healthy recording of silence.
   */
  sourcesLive(): boolean
  /** Stop every track — microphone, share, and the share's video — and close
   *  the context. Safe to call twice. */
  dispose(): void
}

export interface PrepareRecordingOptions {
  /** Defaults to the microphone; nothing captures the machine unasked. */
  source?: RecordingSource
  /**
   * Fires when an audio track of the source ends (#811) — a capture device
   * disappearing, or the share being stopped from the browser's own control.
   * Attached per track, because a mixed recording carries more than one and
   * losing any of them changes what is being recorded.
   */
  onSourceLost?: () => void
  media?: RecordingMedia
}

/**
 * Acquire what a recording captures and wire up the level analyser.
 *
 * Both entry points — starting fresh and resuming a crashed session — built
 * this by hand, identically. One copy means the next change to how a recording
 * is captured happens once instead of twice, which matters because the two
 * drifting apart is what silently changes a session's configuration mid-way.
 */
export async function prepareRecordingStream(
  options: PrepareRecordingOptions = {}
): Promise<PreparedRecordingStream> {
  const media = options.media ?? browserMedia
  return options.source === 'microphone+system'
    ? prepareMixed(media, options)
    : prepareMicrophone(media, options)
}

/** Stop a stream's tracks without caring whether it exists. */
function stopAll(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach((track) => track.stop())
}

function watchEnd(stream: MediaStream, onSourceLost: (() => void) | undefined): void {
  if (!onSourceLost) return
  stream.getAudioTracks().forEach((track) => {
    track.addEventListener('ended', onSourceLost, { once: true })
  })
}

/** Close a context once; a second close throws in some browsers. */
function closeOnce(context: AudioContext): void {
  if (context.state !== 'closed') void context.close()
}

async function prepareMicrophone(
  media: RecordingMedia,
  options: PrepareRecordingOptions
): Promise<PreparedRecordingStream> {
  // Browser defaults on purpose — echo cancellation and noise suppression are
  // right for a microphone in a room, and only the mixed path must forgo them.
  const stream = await media.getUserMedia({ audio: true })
  watchEnd(stream, options.onSourceLost)

  try {
    const context = media.createAudioContext()
    const analyser = context.createAnalyser()
    analyser.fftSize = ANALYSER_FFT_SIZE
    const gain = context.createGain()
    context.createMediaStreamSource(stream).connect(gain).connect(analyser)

    return {
      stream,
      context,
      analyser,
      source: 'microphone',
      gains: { microphone: gain },
      sourcesLive: () => hasLiveAudioTrack(stream),
      dispose: () => {
        stopAll(stream)
        closeOnce(context)
      },
    }
  } catch (error) {
    // The microphone is already open and the caller never gets a handle, so
    // nothing else could release it — the capture indicator would stay lit for
    // a recording that never started.
    stopAll(stream)
    throw error
  }
}

async function prepareMixed(
  media: RecordingMedia,
  options: PrepareRecordingOptions
): Promise<PreparedRecordingStream> {
  // The share goes FIRST: getDisplayMedia needs transient user activation, and
  // awaiting a microphone permission prompt before it can spend that.
  //
  // `video: true` is not optional — an audio-only request yields no audio track
  // at all. The picture is never recorded; it exists so the audio can be had.
  const display = await media.getDisplayMedia({
    video: true,
    audio: MIXED_AUDIO_CONSTRAINTS,
    // Not a constraint but a dictionary member, which is why there is no
    // feature detection for it: `systemAudio` does not appear in
    // getSupportedConstraints(), measured on Chrome 153 (#811).
    systemAudio: 'include',
    windowAudio: 'window',
  } as DisplayMediaStreamOptions)

  if (display.getAudioTracks().length === 0) {
    stopAll(display)
    throw new SystemAudioMissingError()
  }

  let mic: MediaStream | null = null
  let context: AudioContext | null = null
  try {
    mic = await media.getUserMedia({ audio: MIXED_AUDIO_CONSTRAINTS })

    context = media.createAudioContext()
    const destination = context.createMediaStreamDestination()

    const micGain = context.createGain()
    const systemGain = context.createGain()
    context.createMediaStreamSource(mic).connect(micGain).connect(destination)
    context
      .createMediaStreamSource(new MediaStream(display.getAudioTracks()))
      .connect(systemGain)
      .connect(destination)

    const analyser = context.createAnalyser()
    analyser.fftSize = ANALYSER_FFT_SIZE
    // Fed from the mix, so the waveform shows what is recorded rather than one
    // half of it.
    context.createMediaStreamSource(destination.stream).connect(analyser)

    watchEnd(mic, options.onSourceLost)
    watchEnd(display, options.onSourceLost)

    const micStream = mic
    const ctx = context
    return {
      stream: destination.stream,
      context: ctx,
      analyser,
      source: 'microphone+system',
      displaySurface: display.getVideoTracks()[0]?.getSettings().displaySurface,
      gains: { microphone: micGain, system: systemGain },
      // Both, not either: a meeting capture that quietly carries on as
      // microphone-only is the failure this whole feature exists to avoid.
      sourcesLive: () => hasLiveAudioTrack(micStream) && hasLiveAudioTrack(display),
      dispose: () => {
        stopAll(micStream)
        // Includes the video track — that is what ends the share, and what
        // turns off the browser's sharing bar.
        stopAll(display)
        closeOnce(ctx)
      },
    }
  } catch (error) {
    stopAll(mic)
    stopAll(display)
    if (context) closeOnce(context)
    throw error
  }
}

/**
 * Can this browser be ASKED to share the computer's audio? (#811)
 *
 * Only the coarse question, because the fine one has no answer: `systemAudio`
 * is a dictionary member of the getDisplayMedia options, not a constraint, so
 * it does not appear in `getSupportedConstraints()` — measured on Chrome 153.
 * There is nothing to probe for.
 *
 * So this hides the option only where the API is absent entirely. Firefox and
 * Safari DO expose `getDisplayMedia` and pass this check while never handing
 * over system audio; that case surfaces as `SystemAudioMissingError` after the
 * attempt, which is why its message must name both possible causes rather than
 * assuming a checkbox the browser never showed.
 *
 * Deliberately not user-agent sniffing: the issue asks for feature detection,
 * and a UA list would rot the moment a browser gains the capability.
 */
export function canRequestSystemAudio(): boolean {
  return typeof navigator?.mediaDevices?.getDisplayMedia === 'function'
}
